import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DictionaryEntry } from '../types';
import { Header } from './Header';
import { Footer } from './Footer';
import { DefinitionFooter } from './DefinitionFooter';

export const DefinitionPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [entry, setEntry] = useState<DictionaryEntry | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEntry = async () => {
            if (!slug) return;
            setLoading(true);

            // On cherche par le terme normalisé (minuscule)
            const term = decodeURIComponent(slug).toLowerCase();

            const { data, error } = await supabase
                .from('dictionary_entries')
                .select('*')
                .ilike('term', term)
                .single();

            if (error) {
                console.error('Error fetching entry:', error);
            } else if (data) {
                setEntry({
                    ...data,
                    fullDefinition: data.full_definition,
                    image: data.image_url
                });
            }
            setLoading(false);
        };

        fetchEntry();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
                <Loader2 className="animate-spin text-indigo-600" size={32} />
                <p className="text-slate-500 font-medium italic">Chargement du terme...</p>
            </div>
        );
    }

    if (!entry) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <h1 className="text-3xl font-serif font-bold text-slate-800 mb-4">Terme introuvable</h1>
                <p className="text-slate-500 mb-8 max-w-md">Désolé, nous n'avons pas trouvé de définition pour "{decodeURIComponent(slug || '')}".</p>
                <Link
                    to="/"
                    className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all hover:-translate-y-1 active:scale-95"
                >
                    Retour au dictionnaire
                </Link>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <Header />

            <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
                <button
                    onClick={() => navigate('/')}
                    className="group flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-8"
                >
                    <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    <span>Retour au dictionnaire</span>
                </button>

                <article className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-5xl md:text-7xl font-sans font-black text-amber-500 tracking-tight">
                            {entry.term}
                        </h1>
                    </div>

                    {/* Définition courte (Lead - Style Papier) */}
                    <div className="mb-10">
                        <p className="text-2xl md:text-3xl text-sky-500 font-sans leading-tight">
                            <span className="font-bold mr-3">{entry.type}</span>
                            {entry.definition.split(' ').slice(0, -1).join(' ')} <span className="whitespace-nowrap">{entry.definition.split(' ').slice(-1)}</span>
                        </p>
                    </div>

                    {/* Image d'illustration */}
                    {entry.image && (
                        <div className="mb-12 rounded-3xl overflow-hidden shadow-sm max-w-2xl mx-auto border border-slate-100 bg-slate-50">
                            <div className="aspect-square w-full">
                                <img
                                    src={entry.image}
                                    alt={`Illustration pour ${entry.term}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    )}

                    {/* Définition complète */}
                    <div className="prose prose-slate prose-lg md:prose-xl max-w-none text-slate-800">
                        {entry.fullDefinition.split(/\n|\\n/).map((paragraph, index) => (
                            <p key={index} className="leading-relaxed mb-6 last:mb-0">
                                {paragraph.trim()}
                            </p>
                        ))}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-3 pt-8 mt-12 border-t border-slate-100">
                        {entry.tags.map((tag) => (
                            <span
                                key={tag}
                                className="px-4 py-2 bg-slate-50 text-slate-500 text-sm font-semibold rounded-2xl border border-slate-100"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* Footer */}
                    <DefinitionFooter entry={entry} />
                </article>
            </main>

            <Footer />
        </div>
    );
};
